<script setup lang="ts">
/** EDD 문서 본문 — Word 템플릿 구조 그대로. CddDocVarField + CDD_DOC_CTX. 하단 [[signers]]. */
import { inject } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import CddDocVarField from '../cdd/CddDocVarField.vue';
import { CDD_DOC_CTX } from '../cdd/cddContext';
import SignersField from '../resignation/signers/SignersField.vue';
import { EDD_APPROVAL_OPTIONS } from './eddFields';
import EddHeader from './EddHeader.vue';

const props = withDefaults(defineProps<{
  entityCd?: string;
  signers?: SignerEntry[];
}>(), {
  entityCd: '',
  signers: () => [],
});

const emit = defineEmits<{
  (e: 'update:signers', list: SignerEntry[]): void;
}>();

const ctx = inject(CDD_DOC_CTX)!;
</script>

<template>
  <div>
    <EddHeader />

    <table class="cdd-tbl" style="margin-top:14px">
      <tbody>
        <tr>
          <td class="cdd-lcell">Individual name of higher risk customer/PEP <span class="cdd-cn">高风险客户/政治公众人物姓名</span></td>
          <td><CddDocVarField field-key="edd_pep_name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Customer/PEP's role in Legal Person/Legal Arrangement, where relevant <span class="cdd-cn">客户/政治公众人物在法人/法律安排中的角色（如相关）</span></td>
          <td><CddDocVarField field-key="edd_pep_role" /></td>
        </tr>
      </tbody>
    </table>

    <table class="cdd-tbl" style="margin-top:14px">
      <tbody>
        <tr>
          <td class="cdd-sec" colspan="2">For higher ML/TF/PF risk customers <span class="cdd-cn">对于较高洗钱/恐融/扩融风险客户</span></td>
        </tr>
        <tr>
          <td class="cdd-lcell">
            Source of Fund <span class="cdd-cn">资金来源</span> / Source of Wealth <span class="cdd-cn">财富来源</span>
            <div class="cdd-intro" style="margin-top:4px">In the case of PEPs, both sources must be obtained <span class="cdd-cn">对于政治公众人物（PEPs），必须同时获取两种来源</span></div>
          </td>
          <td><CddDocVarField field-key="edd_source_of_fund_wealth" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">
            Additional Information on Customer and Beneficial Owner <span class="cdd-cn">客户及实益所有人的额外信息</span>
            <div class="cdd-intro" style="margin-top:4px">E.g. volume of assets and other information from public database, or customer declaration <span class="cdd-cn">例如资产规模及来自公共数据库的其他信息，或客户声明</span></div>
          </td>
          <td><CddDocVarField field-key="edd_additional_info" /></td>
        </tr>
        <tr>
          <td class="cdd-sec" colspan="2">For customer subject to EDD – To be approved by Senior Management of the Firm <span class="cdd-cn">对须进行 EDD 的客户 – 须经公司高级管理层批准</span></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Approval <span class="cdd-cn">批准</span></td>
          <td>
            <span
              v-for="o in EDD_APPROVAL_OPTIONS"
              :key="o.value"
              class="cdd-cbx"
              @click="ctx.setCheckbox(o.value)"
            >
              <v-icon size="15" class="box" :color="ctx.checkboxValue.value === o.value ? 'primary' : ''">
                {{ ctx.checkboxValue.value === o.value ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
              {{ o.label }}
              <span class="cdd-cn">{{ o.value === 'edd_approved' ? '批准' : '不批准' }}</span>
            </span>
            <div style="margin-top:6px">Justification <span class="cdd-cn">理由</span>: <CddDocVarField field-key="edd_justification" /></div>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Name of Senior Management <span class="cdd-cn">高级管理层姓名</span></td>
          <td><CddDocVarField field-key="edd_senior_mgmt_name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Position/Designation <span class="cdd-cn">职位/职衔</span></td>
          <td><CddDocVarField field-key="edd_senior_mgmt_designation" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date <span class="cdd-cn">日期</span></td>
          <td><CddDocVarField field-key="edd_approval_date" /></td>
        </tr>
      </tbody>
    </table>

    <div class="edd-signers">
      <SignersField
        :model-value="props.signers"
        :entity-cd="props.entityCd"
        var-key="signers"
        @update:model-value="emit('update:signers', $event)"
      />
    </div>
  </div>
</template>

<style scoped>
.edd-signers { margin-top: 22px; }
</style>
